theme.headerSection = (function () {
  function settleOpeningFocus(panel, opener, activeClass, chooseTarget) {
    let cancelled = false;
    let firstFrame;
    let secondFrame;
    let timer;
    const cancel = () => {
      if (cancelled) return;
      cancelled = true;
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      clearTimeout(timer);
      panel.removeEventListener('transitionend', onTransition);
      panel.removeEventListener('transitioncancel', onTransition);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('pointerdown', cancel, true);
      document.removeEventListener('keydown', onInteraction, true);
    };
    const canMoveFocus = () => document.activeElement === opener || document.activeElement === document.body || document.activeElement === document.documentElement;
    const attempt = () => {
      if (cancelled) return;
      if (!panel.isConnected || panel.inert || !panel.classList.contains(activeClass) || panel.contains(document.activeElement) || !canMoveFocus()) { cancel(); return; }
      const target = chooseTarget();
      if (!target?.getClientRects().length || ['hidden', 'collapse'].includes(getComputedStyle(target).visibility)) return;
      if (typeof document.elementFromPoint === 'function') {
        const rect = target.getBoundingClientRect();
        const topElement = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
        if (!topElement || !target.contains(topElement)) return;
      }
      target.focus({ preventScroll: true });
      if (panel.contains(document.activeElement)) cancel();
    };
    const onTransition = (event) => { if (event.target === panel) attempt(); };
    const onFocusIn = () => { if (panel.contains(document.activeElement) || !canMoveFocus()) cancel(); };
    const onInteraction = (event) => { if (!['Tab', 'Escape'].includes(event.key)) cancel(); };
    panel.addEventListener('transitionend', onTransition);
    panel.addEventListener('transitioncancel', onTransition);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('pointerdown', cancel, true);
    document.addEventListener('keydown', onInteraction, true);
    panel.getBoundingClientRect();
    const style = getComputedStyle(panel);
    const milliseconds = (value) => (Number.parseFloat(value) || 0) * (value.trim().endsWith('ms') ? 1 : 1000);
    const durations = (style.transitionDuration || '0s').split(',').map(milliseconds);
    const delays = (style.transitionDelay || '0s').split(',').map(milliseconds);
    const duration = Math.max(0, ...durations.map((value, index) => value + delays[index % delays.length]));
    timer = setTimeout(() => { attempt(); cancel(); }, duration + 80);
    firstFrame = requestAnimationFrame(() => {
      attempt();
      if (!cancelled) secondFrame = requestAnimationFrame(attempt);
    });
    return cancel;
  }
  function header() {
    const panel = document.querySelector('.offcanvas-header');
    const triggers = Array.from(document.querySelectorAll('.header__actions_btn--menu'));
    const closeButton = panel?.querySelector('.offcanvas__close_btn');
    if (!panel || !closeButton || !triggers.length || panel.dataset.irMenuBound) return;
    panel.dataset.irMenuBound = 'true';
    const events = new AbortController();
    const options = { signal: events.signal };
    let opener;
    let previousOverflow;
    let background = [];
    let cancelPendingFocus;
    const visible = (element) => element && !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
      element.getClientRects().length > 0 && !['hidden', 'collapse'].includes(getComputedStyle(element).visibility);
    const focusable = () => Array.from(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]'))
      .filter((element) => element.tabIndex >= 0 && visible(element));
    const setExpanded = (menu, expanded) => {
      const toggle = menu.nextElementSibling;
      menu.hidden = !expanded;
      menu.style.display = expanded ? 'block' : 'none';
      menu.parentElement.classList.toggle('active', expanded);
      toggle.classList.toggle('active', expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      if (!expanded) menu.querySelectorAll('.offcanvas__sub_menu').forEach((child) => setExpanded(child, false));
    };
    document.querySelectorAll('.offcanvas__menu').forEach((menuRoot, rootIndex) => {
      menuRoot.querySelectorAll('.offcanvas__sub_menu').forEach((menu, menuIndex) => {
        if (!menu.id) menu.id = `IRMenuSubmenu-${rootIndex}-${menuIndex}`;
        let toggle = menu.nextElementSibling;
        if (!toggle?.classList.contains('offcanvas__sub_menu_toggle')) {
          toggle = document.createElement('button');
          toggle.className = 'offcanvas__sub_menu_toggle';
          menu.insertAdjacentElement('afterend', toggle);
        }
        toggle.type = 'button';
        toggle.setAttribute('aria-controls', menu.id);
        toggle.setAttribute('aria-label', menu.previousElementSibling.textContent.trim());
        toggle.setAttribute('aria-expanded', 'false');
        menu.hidden = true;
        menu.style.display = 'none';
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          if (!expanded) {
            Array.from(menu.parentElement.parentElement.children).forEach((sibling) => {
              if (sibling !== menu.parentElement) {
                const siblingMenu = Array.from(sibling.children).find((child) => child.classList.contains('offcanvas__sub_menu'));
                if (siblingMenu) setExpanded(siblingMenu, false);
              }
            });
          }
          setExpanded(menu, !expanded);
        }, options);
      });
    });
    const close = (restoreFocus = true) => {
      cancelPendingFocus?.();
      cancelPendingFocus = null;
      if (!panel.classList.contains('open')) return;
      panel.classList.remove('open');
      panel.removeAttribute('aria-modal');
      background.forEach(([element, wasInert]) => { element.inert = wasInert; });
      background = [];
      document.body.style.overflowY = previousOverflow;
      triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      if (restoreFocus) {
        const fallback = document.querySelector('.header__logo_link');
        if (visible(opener)) opener.focus();
        else if (visible(fallback)) fallback.focus();
      }
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
    };
    const open = (trigger) => {
      if (panel.classList.contains('open')) return;
      opener = trigger;
      previousOverflow = document.body.style.overflowY;
      panel.inert = false;
      panel.removeAttribute('aria-hidden');
      panel.classList.add('open');
      panel.setAttribute('aria-modal', 'true');
      document.body.style.overflowY = 'hidden';
      triggers.forEach((item) => item.setAttribute('aria-expanded', 'true'));
      let branch = panel;
      while (branch.parentElement && branch !== document.body) {
        Array.from(branch.parentElement.children).forEach((sibling) => {
          if (sibling !== branch && !sibling.classList.contains('offcanvas-overlay') && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
            background.push([sibling, sibling.inert]);
            sibling.inert = true;
          }
        });
        branch = branch.parentElement;
      }
      cancelPendingFocus = settleOpeningFocus(panel, opener, 'open', () => closeButton);
    };
    panel.inert = true;
    panel.setAttribute('aria-hidden', 'true');
    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.addEventListener('click', (event) => { event.preventDefault(); open(trigger); }, options);
    });
    closeButton.addEventListener('click', (event) => { event.preventDefault(); close(); }, options);
    document.addEventListener('click', (event) => {
      if (!panel.contains(event.target) && !triggers.some((trigger) => trigger.contains(event.target))) close();
    }, options);
    document.addEventListener('keydown', (event) => {
      if (!panel.classList.contains('open')) return;
      if (!panel.contains(document.activeElement) && document.activeElement !== opener && document.activeElement !== document.body && document.activeElement !== document.documentElement) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
      } else if (event.key === 'Tab') {
        const elements = focusable();
        const first = elements[0] || panel;
        const last = elements[elements.length - 1] || panel;
        if (!elements.length || !panel.contains(document.activeElement) || (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      }
    }, options);
    window.addEventListener('resize', () => { if (window.innerWidth >= 992) close(); }, options);
    this.onUnload = () => { close(false); events.abort(); delete panel.dataset.irMenuBound; };
  }
  return header;
})();
